import React, { useState, useEffect } from 'react';
import { ENDPOINTS } from 'constants/endpoints';
import { Col, Row } from 'antd';
import stpImage from 'images/stp.svg';
import { useHistory } from "react-router-dom";
import { useMediaQuery } from 'react-responsive';
import { getEncryptedUserId } from '__utils/common-utils';
import { getEventName, Mixpanel } from '__utils/mixpanel';
import PlanIntro, { greyBgPlanDescComponent, headerDescComponent } from 'views/plan-introduction';
import SiteAccordion from 'components/stp-swp-accordion';
import { CustomisedHr } from 'views/common-styled-components';
import { SuccessBtnWhiteGreenBorder } from 'components/button';
import ConfirmationModal from 'components/confirmation-modal';
import { LayoutContext } from 'components/app-routes';
import resolveURLParams from '__utils/url-params-resolver';
import { getDefaultHeaders, post, get } from '__utils/ajax-wrapper';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { SubmitButton, WhiteGreenBorderBtn, DetailsCardContainer, GreenBgSmallBtn, proceedAfterBseOrder, StpSwpHelper } from '../stp-swp-helper';
import useFlashToggler from 'hooks/use-flash-toggler';
import { AGENT_STATUS } from 'constants/common-constants';


const StpDetails: React.FC = () => {
    const history = useHistory();
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const { saveBSEorder, } = StpSwpHelper();
    const [presentStpsList, setPresentStpsList] = useState<Array<any>>([]);
    const [ShowCancelPlanPopup, setShowCancelPlanPopup] = useState<boolean>(false);
    const { layoutProps, SetLayoutProps } = React.useContext(LayoutContext);
    const [stpItemToBeOperated, setStpItemToBeOperated] = useState<any>({});
    const [showConfirmPopup, setShowConfirmPopup] = useState<Boolean>(false);
    const flash = useFlashToggler();
    const freqObject: any = { 1: "monthly", 3: "quarterly", 6: "half yearly", 12: "yearly", 30: "daily", 52: "weekly" };

    const stpDescObj = {
        image: stpImage,
        title: 'Systematic Transfer Plan (STP)',
        description: 'Setup regular transfer of a fixed amount at a fixed interval from one Mutual Fund to another.',
        question: 'Why should one setup STP?',
        answers: ['Ideal for individuals looking to invest in a lumpsum but are not ready to do that at one go in a volatile market.',
            'Allows you to transfer your money from debt fund to equity fund regularly rather than keeping it in a bank’s saving account.',
        ]
    };

    useEffect(() => {
        SetLayoutProps({
            config: { title: 'Systematic Transfer Plan' }
        });
        getAllSTPorders();
        Mixpanel.track(getEventName("viewMyStps", "loaded", "profile"));

    }, []);

    //purpose: to be executed after otp is verified, output: calls saveBseOrder with transaction id to setup order
    const proceedAfterVerification = (stpItem: any) => {
        saveBSEorder(stpItem?.id, onBseOrderSuccess, stpItem, onBSEfailure);
    }

    //purpose: to be call if punched successfully
    const onBseOrderSuccess = () => {
        setShowConfirmPopup(true);
    }

    const onBSEfailure = () => {
        window.location.reload();
    }

    //purpose: passed as params to hide confirm popup
    const onConfirmationClicked = () => {
        setShowConfirmPopup(false);
        window.location.reload();
    }

    //purpose: gets all stp orders,output: store all stps in presentStpsList
    const getAllSTPorders = () => {
        get(resolveURLParams(ENDPOINTS.GET_ALL_STP_SWP_ORDERS, { trxnType: "STP" }, { ucc: getEncryptedUserId() }),
            getDefaultHeaders(),
            true
        ).subscribe((response: AjaxResponse | AjaxError) => {
            if (response?.response?.data) {
                setPresentStpsList(response?.response?.data);
            }
        }, (error: AjaxError) => {
            console.log(error);
        });
    }

    //purpose:  common stps accordion component since its placement is different in different resolutions
    const activeSTPsComponent = () => {
        return (
            presentStpsList.sort((a: any, b: any) => b.id - a.id).map((element: any, index: number) => (
                <SiteAccordion accordionItem={element} key={index}
                    footerBtn={
                        (element.agentStatus === AGENT_STATUS.ACTIVE) ?
                            <SuccessBtnWhiteGreenBorder
                                onClick={() => onCancelStpClicked(element)}
                                type="button">
                                Stop
                            </SuccessBtnWhiteGreenBorder> : (element.agentStatus === AGENT_STATUS.APPROVED.toUpperCase()) ?
                                <SuccessBtnWhiteGreenBorder
                                    onClick={() => { setStpItemToBeOperated(element); proceedAfterVerification(element) }}
                                    type="button">
                                    Setup order
                                </SuccessBtnWhiteGreenBorder> : <></>
                    }

                />
            ))
        )
    }

    // purpose: when cancel stp is clicked,params: specific stp oreder that needs cancelling, output: store specific stp order in stpItem , shows delete confirmation popup
    const onCancelStpClicked = (stpItem: any) => {
        Mixpanel.track(getEventName("stopSTP", "clicked", "mySTPs"));
        setStpItemToBeOperated(stpItem);
        setShowCancelPlanPopup(true);
    }

    //purpose: cancels purchased order, output: calls getActiveSTPorders() to fetch latest orders
    const onCancelStpConfirmed = () => {
        setShowCancelPlanPopup(false);
        post(resolveURLParams(ENDPOINTS.POST_STP_SWP_CANCELLATION, { txnRegNo: stpItemToBeOperated?.id, txnType: 'STP' }, { ucc: getEncryptedUserId() }), {}, getDefaultHeaders(), true)
            .subscribe((response: AjaxResponse | AjaxError) => {
                if (response?.response?.data) {
                    flash.success('STP stopped successfully !');
                    window.location.reload();
                }
                else if(response?.response?.meta?.message === 'failure'){
                    flash.error('Failed to stop STP !')
                }
            }, (error: AjaxError) => {
                flash.error("Unable to stop STP. Please try later.")
            });
    }

    //purpose:  since placement of Active STPs are different in mobile n desktop, it returns a common component
    const commonActiveComponent = () => {
        return <>
            <p className="greyTxt">My STP’s</p>
            <CustomisedHr style={{ marginBottom: "4px" }} />
            {activeSTPsComponent()}
            <br />
        </>
    }

    return (<div>
        <DetailsCardContainer isMobile={isMobile}>
            <div>
                <div className="text">
                    {(presentStpsList?.length > 0) ?
                        <div>
                            <Row gutter={!isMobile ? 48 : 0}>
                                <Col xs={24} md={15}>
                                    {headerDescComponent(stpDescObj, isMobile, history)}
                                    {!isMobile && commonActiveComponent()}
                                </Col>

                                <Col xs={24} md={9}>
                                    {greyBgPlanDescComponent(stpDescObj, isMobile)}
                                    {isMobile && commonActiveComponent()}
                                </Col>
                            </Row>
                        </div> :
                        <div>
                            <PlanIntro planObject={stpDescObj} noActive="STP" />
                        </div>
                    }
                </div>
            </div>

            {isMobile && <div className="sticky-footer">
                <SubmitButton width={isMobile ? '' : '200px'} onClick={() => { Mixpanel.track(getEventName("setupNewSTP", "clicked", "profile")); history.push('stps/create-new-stp') }}>Setup New STP</SubmitButton>
            </div>}

        </DetailsCardContainer>

        {showConfirmPopup && proceedAfterBseOrder(onConfirmationClicked, stpItemToBeOperated?.firstOrderToday, stpItemToBeOperated?.startDate)}

        {ShowCancelPlanPopup && <ConfirmationModal
            title='Confirmation to Stop'
            modalBody={`Are you sure you want to stop ${stpItemToBeOperated?.noOfInstallments ? stpItemToBeOperated?.noOfInstallments : ''} ${freqObject[stpItemToBeOperated?.investmentFrequency]} transfers of ₹ ${stpItemToBeOperated?.installmentAmt.toLocaleString()} ?`}
            onCancel={() => setShowCancelPlanPopup(false)}
            footerButtons={
                <>
                    <WhiteGreenBorderBtn isMobile={isMobile} onClick={() => setShowCancelPlanPopup(false)}>
                        Cancel
                    </WhiteGreenBorderBtn>
                    <GreenBgSmallBtn onClick={onCancelStpConfirmed}>
                        Stop STP
                    </GreenBgSmallBtn>
                </>
            } />
        }

    </div>);
};

export default StpDetails;

